import LoadingButton from '@mui/lab/LoadingButton';
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Theme,
    Typography,
} from '@mui/material';
import Joi from 'joi';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { InventoryVersion } from '../../../../hooks/useInventory';
import {
    SextforceAutoCampaignTipReply,
    SextforceAutoCampaignTipReplyMessageDetails,
    SextforceAutoCampaignTipReplyMessageDetailsSchema,
    SextforceAutoCampaignTipReplyWithMessageDetails,
} from '../../../../hooks/useSextforceAutoCampaignTipReply';
import useSextforceBigBrotherRestrictedWords from '../../../../hooks/useSextforceBigBrotherRestrictedWords';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import useSubscriberLists from '../../../../hooks/useSubscriberLists';
import useSubscriberMedia from '../../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditonal from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { handleHttpError, isSextforceActive, parseOnlyFansListId } from '../../../../utils/common';
import { getRestrictedWordsInMessage } from '../../../../utils/messageUsers';
import OnlyFansListsSelector from '../../../common/OnlyFansListsSelector';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';
import QuickSelectInventory from '../../../inventory/QuickSelectInventory';
import MessageUsersFormAddMedias from '../../messageUsers/sendMessage/MessageUsersFormAddMedias';
import MessageUsersFormEditor from '../../messageUsers/sendMessage/MessageUsersFormEditor';
import MessageUsersPriceLock from '../../messageUsers/sendMessage/MessageUsersPriceLock';
import MessageUsersTagCreators from '../../messageUsers/sendMessage/MessageUsersTagCreators';

type Props = {
    subscriber: OnlyFansSubscriber;
    autoCampaignTipReply: SextforceAutoCampaignTipReplyWithMessageDetails;
    initialValues: SextforceAutoCampaignTipReplyMessageDetails | null;
    tipOption: number;
    theme: Theme;
    open: boolean;
    onClose: () => void;
    createAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    updateAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    refetchAutoCampaignTipReplies: () => void;
};

const SextforceAutoCampaignTipReplyMessageDetailsDialog = (props: Props) => {
    const {
        subscriber,
        autoCampaignTipReply,
        initialValues,
        tipOption,
        theme,
        open,
        onClose,
        createAutoCampaignTipReplyTipMessageDetails,
        updateAutoCampaignTipReplyTipMessageDetails,
        refetchAutoCampaignTipReplies,
    } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();

    // Loading state
    const [isSaving, setIsSaving] = useState(false);

    // Message details
    const [replyMessageDetails, setReplyMessageDetails] = useState<SextforceAutoCampaignTipReplyMessageDetails>(
        initialValues || {
            autoCampaignReplyId: autoCampaignTipReply._id!,
            name: undefined,
            tipAmount: tipOption,
            listId: undefined,
            autoFollowBackUser: undefined,
            existingTippersProcessed: undefined,
            messageDetails: {
                message: '',
                lockMessagePrice: null,
                lockedText: false,
                mediaFiles: [],
                mediaFilesPreviews: [],
                replaceUsername: null,
                taggedCreators: [],
                taggedReleaseForms: [],
            },
            replyCount: 0,
        },
    );

    // Errors
    const errors: Joi.ValidationErrorItem[] | null =
        SextforceAutoCampaignTipReplyMessageDetailsSchema.validate(replyMessageDetails).error?.details || null;

    // Lists
    const [addTipperToList, setAddTipperToList] = useState<boolean>(false);
    const { lists, isLoading: listsLoading } = useSubscriberLists(addTipperToList);

    // Media
    const { getThumbnail: getSubscriberThumbnail, getAudio: getSubscriberAudio } = useSubscriberMedia();

    // Form
    const [includeMedia, setIncludeMedia] = useState<boolean>(replyMessageDetails.messageDetails.mediaFiles.length > 0 ? true : false);
    const [vaultAlbum, setVaultAlbum] = useState<number>(-1);
    const [medias, setMedias] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>(
        replyMessageDetails.messageDetails.mediaFiles || [],
    );

    const [vaultAlbumName, setVaultAlbumName] = useState<string | undefined>(undefined);
    const [mediasPreviews, setMediasPreviews] = useState<number[]>(replyMessageDetails.messageDetails.mediaFilesPreviews || []);
    const [thumbnails, setThumbnails] = useState<any[]>([]);
    const [tagCreators, setTagCreators] = useState<boolean>(replyMessageDetails.messageDetails.taggedCreators.length > 0 ? true : false);
    const [taggedCreators, setTaggedCreators] = useState<{ id: number; name: string }[]>(
        replyMessageDetails.messageDetails.taggedCreators || [],
    );
    const [taggedReleaseForms, setTaggedReleaseForms] = useState<{ id: number; name: string }[]>(
        replyMessageDetails.messageDetails.taggedReleaseForms || [],
    );
    const [lockMessage, setLockMessage] = useState<boolean>(replyMessageDetails.messageDetails.lockMessagePrice !== null ? true : false);
    const [lockedText, setLockedText] = useState<boolean>(replyMessageDetails.messageDetails.lockedText || false);
    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');
    const [selectedIventory, setSelectedInventory] = useState<InventoryVersion>();
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditonal();

    const taggedCreatorsCount = replyMessageDetails.messageDetails.taggedCreators.length;
    const taggedReleaseFormsCount = replyMessageDetails.messageDetails.taggedReleaseForms
        ? replyMessageDetails.messageDetails.taggedReleaseForms.length
        : 0;
    const totalTagged = taggedCreatorsCount + taggedReleaseFormsCount;

    useEffect(() => {
        const switchInventory = async () => {
            if (selectedIventory) {
                const oldMessageDetails = { ...replyMessageDetails };
                oldMessageDetails.listId = undefined;
                oldMessageDetails.messageDetails.lockMessagePrice = 0;
                oldMessageDetails.messageDetails.taggedCreators = [];
                oldMessageDetails.messageDetails.mediaFiles = [];
                oldMessageDetails.messageDetails.mediaFilesPreviews = [];
                setAddTipperToList(false);
                setIncludeMedia(false);
                setMedias([]);
                setMediasPreviews([]);
                setThumbnails([]);
                setTagCreators(false);
                setTaggedCreators([]);
                setTaggedReleaseForms([]);
                setLockMessage(false);
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);

                oldMessageDetails.messageDetails.message = selectedIventory.text;
                if (selectedIventory.autoAddList) {
                    setAddTipperToList(true);
                    oldMessageDetails.listId = Number(selectedIventory.autoAddList);
                }

                if (Number(selectedIventory.price) > 0) {
                    setLockMessage(true);
                    oldMessageDetails.messageDetails.lockMessagePrice = Number(selectedIventory.price);
                }
                if (selectedIventory.creatorTags && selectedIventory.creatorTags.length > 0) {
                    setTagCreators(true);
                    setTaggedCreators(selectedIventory.creatorTags.map(item => ({ id: item.creatorOnlyfansId, name: item.creatorName })));
                    oldMessageDetails.messageDetails.taggedCreators = selectedIventory.creatorTags.map(item => ({
                        id: item.creatorOnlyfansId,
                        name: item.creatorName,
                    }));
                }
                setReplyMessageDetails(oldMessageDetails);
                if ((selectedIventory.unlockedMedias?.length ?? 0) > 0 || (selectedIventory.lockedMedias?.length ?? 0) > 0) {
                    setIncludeMedia(true);
                    const grabMedias = await getManyVaultMediaFromDatabase([
                        ...(selectedIventory.lockedMedias?.map(item => item.mediaId) ?? []),
                        ...(selectedIventory.unlockedMedias?.map(item => item.mediaId) ?? []),
                    ]);
                    oldMessageDetails.messageDetails.mediaFiles = grabMedias;
                    setMedias(grabMedias);
                    if ((selectedIventory.unlockedMedias?.length ?? 0) > 0) {
                        setMediasPreviews(selectedIventory.unlockedMedias?.map(item => item.mediaId) ?? []);
                        oldMessageDetails.messageDetails.mediaFilesPreviews =
                            selectedIventory.unlockedMedias?.map(item => item.mediaId) ?? [];
                    }
                }
                setReplyMessageDetails(oldMessageDetails);
            }
        };
        switchInventory();
    }, [selectedIventory]);

    useEffect(() => {
        if (open) {
            setReplyMessageDetails(
                initialValues || {
                    autoCampaignReplyId: autoCampaignTipReply._id!,
                    name: undefined,
                    tipAmount: tipOption,
                    listId: undefined,
                    autoFollowBackUser: undefined,
                    existingTippersProcessed: undefined,
                    messageDetails: {
                        message: '',
                        lockMessagePrice: null,
                        lockedText: false,
                        mediaFiles: [],
                        mediaFilesPreviews: [],
                        replaceUsername: null,
                        taggedCreators: [],
                    },
                    replyCount: 0,
                },
            );

            setAddTipperToList(initialValues ? (initialValues.listId ? true : false) : false);
            setIncludeMedia(
                initialValues
                    ? initialValues.messageDetails.mediaFiles.length > 0
                        ? true
                        : false
                    : replyMessageDetails.messageDetails.mediaFiles.length > 0
                    ? true
                    : false,
            );
            setVaultAlbum(-1);
            setVaultAlbumName(undefined);
            setMedias(initialValues?.messageDetails.mediaFiles || []);
            setMediasPreviews(initialValues?.messageDetails.mediaFilesPreviews || []);
            setThumbnails([]);
            setTagCreators(
                initialValues
                    ? initialValues.messageDetails.taggedCreators.length > 0 ||
                      (initialValues.messageDetails.taggedReleaseForms && initialValues.messageDetails.taggedReleaseForms.length > 0)
                        ? true
                        : false
                    : replyMessageDetails.messageDetails.taggedCreators.length > 0 ||
                      (replyMessageDetails.messageDetails.taggedReleaseForms &&
                          replyMessageDetails.messageDetails.taggedReleaseForms.length > 0)
                    ? true
                    : false,
            );
            setTaggedCreators(initialValues ? initialValues.messageDetails.taggedCreators : []);
            setTaggedReleaseForms(
                initialValues && initialValues.messageDetails.taggedReleaseForms ? initialValues.messageDetails.taggedReleaseForms : [],
            );
            setLockMessage(initialValues ? (initialValues.messageDetails.lockMessagePrice !== null ? true : false) : false);
            setMessageHasRestrictedWords(false);
            setMessageFoundRestrictedWords([]);
            setSaveTemplate(false);
            setSaveTemplateName('');
        }
    }, [autoCampaignTipReply._id, initialValues, open]);

    // Check the message doesn't contain any OnlyFans restricted words
    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (text && !isLoadingRestrictedWords && restrictedWords && subscriber) {
                // const lowerCaseMessage = text.toLowerCase();
                // const wordsInMessage = lowerCaseMessage.split(/[\s\n/,.]+/);

                // const restrictedWordsFound = wordsInMessage.filter(word => restrictedWords.has(word));

                // if (subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList?.includes(restrictedWordsFound)) {
                //     // Remove any words that are in the whitelist
                //     restrictedWordsFound.filter(word => !subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList?.includes(word));
                // }
                const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);

                setMessageHasRestrictedWords(restrictedWordsFound.length > 0 ? true : false);
                setMessageFoundRestrictedWords(restrictedWordsFound);
            } else {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
            }
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );

    useEffect(() => {
        setReplyMessageDetails(prevState => {
            return {
                ...prevState,
                messageDetails: {
                    ...prevState.messageDetails,
                    mediaFiles: medias,
                },
            };
        });
    }, [medias]);

    useEffect(() => {
        setReplyMessageDetails(prevState => {
            return {
                ...prevState,
                messageDetails: {
                    ...prevState.messageDetails,
                    mediaFilesPreviews: mediasPreviews,
                },
            };
        });
    }, [mediasPreviews]);

    useEffect(() => {
        setReplyMessageDetails(prevState => {
            return {
                ...prevState,
                messageDetails: {
                    ...prevState.messageDetails,
                    taggedCreators: taggedCreators,
                },
            };
        });
    }, [taggedCreators]);

    useEffect(() => {
        setReplyMessageDetails(prevState => {
            return {
                ...prevState,
                messageDetails: {
                    ...prevState.messageDetails,
                    taggedReleaseForms: taggedReleaseForms,
                },
            };
        });
    }, [taggedReleaseForms]);

    // Media
    const getThumbnail = (thumbId: number, thumbUrl: string) => {
        getSubscriberThumbnail(subscriber, thumbId, thumbUrl)?.then(blob => {
            if (blob) {
                setThumbnails(prevState => {
                    return [...prevState, { thumbId, blob }];
                });
            } else {
                setThumbnails(prevState => {
                    return [...prevState, { thumbId, blob: null }];
                });
            }
        });
    };

    const getAudio = (url: string) => getSubscriberAudio(subscriber, url);

    // Save the message details
    const handleSave = () => {
        const updateAutoRepost = async () => {
            if (
                userContext.jwtToken &&
                settingsContext.apiKey &&
                'userId' in params &&
                params.userId &&
                subscriber &&
                autoCampaignTipReply &&
                autoCampaignTipReply._id
            ) {
                setIsSaving(true);

                if (initialValues) {
                    // Update the existing message details
                    const updatedMessageDetails = await updateAutoCampaignTipReplyTipMessageDetails(
                        autoCampaignTipReply,
                        replyMessageDetails,
                    ).catch(error => {
                        console.error(error);
                        setIsSaving(false);
                        handleHttpError(error, dialog);
                    });

                    if (updatedMessageDetails && updatedMessageDetails._id) {
                        enqueueSnackbar({
                            message: 'Auto Tip Campaign Tip Reply updated successfully.',
                            variant: 'success',
                            autoHideDuration: 3000,
                        });

                        onClose();
                        refetchAutoCampaignTipReplies();
                    } else {
                        enqueueSnackbar({
                            message: 'Auto Tip Campaign Tip Reply could not be updated.',
                            variant: 'error',
                            autoHideDuration: 3000,
                        });
                    }
                } else {
                    // Create a new message details
                    const createdMessageDetails = await createAutoCampaignTipReplyTipMessageDetails(
                        autoCampaignTipReply,
                        replyMessageDetails,
                    ).catch(error => {
                        console.error(error);
                        setIsSaving(false);
                        handleHttpError(error, dialog);
                    });

                    if (createdMessageDetails && createdMessageDetails._id) {
                        enqueueSnackbar({
                            message: 'Auto Tip Campaign Tip Reply created successfully.',
                            variant: 'success',
                            autoHideDuration: 3000,
                        });

                        onClose();
                        refetchAutoCampaignTipReplies();
                    } else {
                        enqueueSnackbar({
                            message: 'Auto Tip Campaign Tip Reply could not be created.',
                            variant: 'error',
                            autoHideDuration: 3000,
                        });
                    }
                }

                setIsSaving(false);
            }
        };

        updateAutoRepost();
    };

    // Close the dialog
    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
            <StyledCardTitleBar title="Add Auto Tip Campaign Tip Reply" />
            <DialogContent>
                <DialogContentText>
                    <Typography variant="h6" gutterBottom>
                        {autoCampaignTipReply.name}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Enter the message details for the tip amount of <strong>${tipOption.toFixed(2)}</strong>. This message will be sent
                        automatically whenever a subscriber tips/contributes this amount to the campaign.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        You can add multiple replies for the same tip amount. They will rotate per fan like a spinning wheel - a fan who
                        tips this amount again will receive a different reply chosen at random until all the replies have been used, at
                        which point the wheel starts over.
                    </Typography>
                </DialogContentText>
                <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <Stack direction="column" spacing={1}>
                                    <Typography variant="h6">Reply Name</Typography>
                                    <Typography variant="body2">
                                        Optional. A short name to identify this reply in the list and in the statistics (e.g. "Wheel Prize
                                        1"). If left blank, the reply will be shown as "Reply 1", "Reply 2", etc.
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Reply Name"
                                        value={replyMessageDetails.name || ''}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={e => {
                                            setReplyMessageDetails(prevState => {
                                                return {
                                                    ...prevState,
                                                    name: e.target.value,
                                                };
                                            });
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <QuickSelectInventory setSelectedInventoryVersion={setSelectedInventory} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={1}
                    flexGrow={0}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(1) }}
                >
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <Grid container spacing={1} flexGrow={1} alignItems="flex-start" sx={{ pr: 1 }}>
                                    <Grid item xs>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="medium"
                                                    checked={addTipperToList}
                                                    onChange={(e, checked) => {
                                                        setAddTipperToList(checked);
                                                    }}
                                                />
                                            }
                                            label={<Typography variant="h6">Add Tippers to List?</Typography>}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction={'column'} spacing={0}>
                                            <Grid container spacing={1} alignItems="center" justifyContent="center">
                                                <Grid item xs>
                                                    <Typography variant="body1" sx={{ marginBottom: theme.spacing(1) }}>
                                                        Select Target List
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Stack direction={'row'} spacing={1}>
                                                <OnlyFansListsSelector
                                                    targetList={
                                                        listsLoading
                                                            ? null
                                                            : lists.find(
                                                                  l =>
                                                                      replyMessageDetails.listId &&
                                                                      parseOnlyFansListId(l.id) ===
                                                                          parseOnlyFansListId(replyMessageDetails.listId),
                                                              ) || null
                                                    }
                                                    setTargetList={listId => {
                                                        if (typeof listId?.id !== 'number') {
                                                            return;
                                                        }

                                                        setReplyMessageDetails(prevState => {
                                                            return {
                                                                ...prevState,
                                                                listId: listId.id as number,
                                                            };
                                                        });
                                                    }}
                                                    disabled={!addTipperToList}
                                                    includeBuiltInLists={false}
                                                    includeSelectNoneOption={true}
                                                    showCreateListButton={true}
                                                    size="small"
                                                />
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <Stack direction="column" spacing={1}>
                                    <div>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="medium"
                                                    checked={replyMessageDetails.autoFollowBackUser || false}
                                                    onChange={(e, checked) => {
                                                        setReplyMessageDetails(prevState => {
                                                            return {
                                                                ...prevState,
                                                                autoFollowBackUser: checked,
                                                            };
                                                        });
                                                    }}
                                                />
                                            }
                                            label={<Typography variant="h6">Auto Follow-Back User?</Typography>}
                                        />
                                    </div>
                                    <Typography variant="body1">
                                        If enabled, the user will be automatically followed back when they tip this amount. Perfect for
                                        those "<em>tip on this campaign to be part of my VIP club</em>" or similar campaigns.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <MessageUsersFormAddMedias
                            subscriber={subscriber}
                            serviceName="autoSendWelcomeMessage"
                            includeMedia={includeMedia}
                            setIncludeMedia={setIncludeMedia}
                            vaultAlbum={vaultAlbum}
                            setVaultAlbum={setVaultAlbum}
                            vaultAlbumName={vaultAlbumName}
                            setVaultAlbumName={setVaultAlbumName}
                            medias={medias}
                            setMedias={setMedias}
                            mediasPreviews={mediasPreviews}
                            setMediasPreviews={setMediasPreviews}
                            thumbnails={thumbnails}
                            getThumbnail={getThumbnail}
                            getAudio={getAudio}
                            tagCreators={tagCreators}
                            setTagCreators={setTagCreators}
                            taggedCreators={taggedCreators}
                            setTaggedCreators={setTaggedCreators}
                            taggedReleaseForms={taggedReleaseForms}
                            setTaggedReleaseForms={setTaggedReleaseForms}
                            session={null}
                            albumsSelectorSize="small"
                            disabled={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {includeMedia && (
                            <MessageUsersTagCreators
                                subscriber={subscriber}
                                tagCreators={tagCreators}
                                setTagCreators={setTagCreators}
                                taggedCreators={taggedCreators}
                                setTaggedCreators={setTaggedCreators}
                                taggedReleaseForms={taggedReleaseForms}
                                setTaggedReleaseForms={setTaggedReleaseForms}
                                session={null}
                                disabled={false}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <MessageUsersPriceLock
                            subscriber={subscriber}
                            lockMessage={lockMessage}
                            setLockMessage={setLockMessage}
                            lockMessagePrice={replyMessageDetails.messageDetails.lockMessagePrice || 0}
                            setLockMessagePrice={lockMessagePrice => {
                                setReplyMessageDetails({
                                    ...replyMessageDetails,
                                    messageDetails: {
                                        ...replyMessageDetails.messageDetails,
                                        lockMessagePrice,
                                    },
                                });
                            }}
                            lockedText={lockedText}
                            setLockedText={lockedTextState => {
                                setReplyMessageDetails({
                                    ...replyMessageDetails,
                                    messageDetails: {
                                        ...replyMessageDetails.messageDetails,
                                        lockedText: lockedTextState,
                                    },
                                });
                            }}
                            medias={medias}
                            thumbnails={thumbnails}
                            mediasPreviews={mediasPreviews}
                            setMediasPreviews={setMediasPreviews}
                            session={null}
                            disabled={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MessageUsersFormEditor
                            subscriber={subscriber}
                            message={replyMessageDetails.messageDetails.message || ''}
                            setMessage={message => {
                                setReplyMessageDetails(prevState => {
                                    return {
                                        ...prevState,
                                        messageDetails: {
                                            ...prevState.messageDetails,
                                            message,
                                        },
                                    };
                                });
                            }}
                            messageHasRestrictedWords={messageHasRestrictedWords}
                            messageFoundRestrictedWords={messageFoundRestrictedWords}
                            checkForRestrictedWords={checkForRestrictedWords}
                            usernameReplacement={replyMessageDetails.messageDetails.replaceUsername || ''}
                            setUsernameReplacement={(replaceUsername: string) => {
                                setReplyMessageDetails(prevState => {
                                    return {
                                        ...prevState,
                                        messageDetails: {
                                            ...prevState.messageDetails,
                                            replaceUsername,
                                        },
                                    };
                                });
                            }}
                            taggedCreators={taggedCreators}
                            taggedReleaseForms={taggedReleaseForms}
                            lockMessage={lockMessage}
                            lockMessagePrice={replyMessageDetails.messageDetails.lockMessagePrice || 0}
                            lockedText={lockedText}
                            saveTemplate={saveTemplate}
                            setSaveTemplate={setSaveTemplate}
                            saveTemplateName={saveTemplateName}
                            setSaveTemplateName={setSaveTemplateName}
                            session={null}
                            borderless={false}
                            theme={theme}
                            disabled={false}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <StyledDialogActions>
                <Button
                    color="error"
                    size="medium"
                    disabled={isSaving}
                    onClick={() => {
                        handleClose({}, 'escapeKeyDown');
                    }}
                >
                    Close
                </Button>
                <LoadingButton
                    color="secondary"
                    variant="contained"
                    size="medium"
                    loading={isSaving}
                    disabled={
                        !!errors ||
                        !autoCampaignTipReply._id ||
                        (messageHasRestrictedWords && isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active) ||
                        (includeMedia && !medias.length) ||
                        (tagCreators && totalTagged === 0) ||
                        (lockMessage &&
                            (!replyMessageDetails.messageDetails.lockMessagePrice ||
                                replyMessageDetails.messageDetails.lockMessagePrice < 3)) ||
                        replyMessageDetails.messageDetails.message.trim().length === 0 ||
                        (addTipperToList && !replyMessageDetails.listId)
                    }
                    onClick={() => {
                        handleSave();
                    }}
                >
                    <span>{initialValues ? 'Update Reply' : 'Create Reply'}</span>
                </LoadingButton>
            </StyledDialogActions>
        </Dialog>
    );
};

export default SextforceAutoCampaignTipReplyMessageDetailsDialog;
